import BrandIdentity, { BrandLogo } from "@/components/theme/BrandIdentity";
import { useTheme } from "@/theme/ThemeProvider";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { api, apiError } from "@/lib/pos-api";
import { getSelectedTenant, selectMasterTenant, setActiveStore as persistStore } from "@/lib/scope";
import { useAuth } from "@/context/AuthContext";
import {
  ScanLine, LayoutDashboard, ShoppingCart, Package, Tags, Users as UsersIcon,
  Receipt, LogOut, Store, ShoppingBag, Truck, Printer, FileBarChart, HandCoins, Boxes, Clock, DatabaseBackup, CreditCard, Server, Palette, ScrollText,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  {section: "Pengaturan", items: [{ to: "/appearance", label: "Tampilan / Tema", icon: Palette, roles: ["admin"] }]},
  {
    section: "Administrasi Master",
    items: [
      { to: "/master/platform", label: "Platform", icon: CreditCard, roles: ["admin"], masterOnly: true },
      { to: "/master/tenants", label: "Kelola Tenant", icon: Store, roles: ["admin"], masterOnly: true },
      { to: "/master/system", label: "Status VPS", icon: Server, roles: ["admin"], masterOnly: true },
      { to: "/master/backup", label: "Backup NAS & Jadwal", icon: DatabaseBackup, roles: ["admin"], masterOnly: true },
    ],
  },
  {
    section: null,
    items: [
      { to: "/pos", label: "Kasir", icon: ShoppingCart, roles: ["admin", "cashier"] },
      { to: "/transactions", label: "Transaksi", icon: Receipt, roles: ["admin", "cashier"] },
      { to: "/piutang", label: "Piutang", icon: HandCoins, roles: ["admin", "cashier"] },
    ],
  },
  {
    section: "Backoffice",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin"] },
      { to: "/reports", label: "Laporan", icon: FileBarChart, roles: ["admin"] },
      { to: "/shifts", label: "Riwayat Shift", icon: Clock, roles: ["admin"] },
      { to: "/warehouse", label: "Penerimaan Gudang", icon: ScanLine, roles: ["admin", "warehouse"] },
      { to: "/whatsapp", label: "WhatsApp Toko", icon: ScanLine, roles: ["admin"], tenantOnly: true },
      { to: "/purchases", label: "Pembelian", icon: ShoppingBag, roles: ["admin"] },
      { to: "/suppliers", label: "Supplier", icon: Truck, roles: ["admin"] },
      { to: "/products", label: "Produk & Stok", icon: Package, roles: ["admin"] },
      { to: "/stok", label: "Stok & Opname", icon: Boxes, roles: ["admin"] },
      { to: "/categories", label: "Kategori", icon: Tags, roles: ["admin"] },
      { to: "/users", label: "Pengguna", icon: UsersIcon, roles: ["admin"] },
      { to: "/stores", label: "Profil Toko", icon: Store, roles: ["admin"] },
      { to: "/backup", label: "Backup & Restore", icon: DatabaseBackup, roles: ["admin"] },
      { to: "/settings", label: "Printer", icon: Printer, roles: ["admin"] },
      { to: "/payment-methods", label: "Metode Pembayaran", icon: CreditCard, roles: ["admin"] },
    ],
  },
];

export default function AppLayout() {
  const { current } = useTheme();
  const { user, logout, tenant, readOnly } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navHidden, setNavHidden] = useState(false);
  useEffect(() => {
    setNavHidden(location.pathname === "/pos");
  }, [location.pathname]);
  const groups = NAV
    .map((g) => ({ ...g, items: g.items.filter((i) => i.roles.includes(user.role) && (!i.masterOnly || tenant?.kind === "master") && (!i.tenantOnly || tenant?.kind === "tenant")) }))
    .filter((g) => g.items.length > 0);

  const [masterTenants, setMasterTenants] = useState([]);
  const selectedTenant = getSelectedTenant();
  const [tenantListError, setTenantListError] = useState("");
  useEffect(() => {
    const load = () => { if (readOnly) api.get("/master/tenants").then((r) => { setMasterTenants(r.data); setTenantListError(""); }).catch(e => setTenantListError(apiError(e))); };
    load(); window.addEventListener("pos-tenants-updated", load);
    return () => window.removeEventListener("pos-tenants-updated", load);
  }, [readOnly]);
  const [stores, setStores] = useState([]);
  useEffect(() => {
    api.get("/stores").then((r) => {
      setStores(r.data);
      persistStore(r.data[0]?.id || '');
    }).catch(() => {});
  }, [user.role, user.store_id]);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const [showLogout, setShowLogout] = useState(false);
  const lpTimer = useRef(null);
  const startLongPress = () => {
    clearTimeout(lpTimer.current);
    lpTimer.current = setTimeout(() => setShowLogout(true), 600);
  };
  const cancelLongPress = () => clearTimeout(lpTimer.current);

  return (
    <div className="h-screen flex bg-ui-canvas text-ui-text overflow-hidden">
      {/* Sidebar */}
      <aside className={`masivi-sidebar w-[72px] xl:w-64 border-r border-ui-line flex flex-col shrink-0${navHidden ? " hidden" : ""}`}>
        <div className="h-16 flex items-center gap-3 px-4 border-b border-ui-line">
          <div className="h-10 w-10 rounded-xl bg-brand-button flex items-center justify-center shrink-0">
            <BrandLogo className="h-6 w-6" />
          </div>
          <div className="hidden xl:block min-w-0">
            <div className="font-heading font-extrabold leading-none truncate">{current.branding.name}</div>
            <div className="text-[10px] text-ui-muted uppercase tracking-widest">Point of Sale</div>
          </div>
        </div>

        <div className="hidden xl:block px-3 py-2 border-b border-ui-line">
          <div className="text-xs text-ui-muted truncate">Toko: <span className="text-ui-text font-semibold">{stores[0]?.name || "—"}</span></div>
        </div>

        <nav className="flex-1 p-2 xl:p-3 space-y-1 overflow-y-auto">
          {groups.map((g, gi) => (
            <div key={gi} className="space-y-1">
              {g.section && (
                <div className="hidden xl:block px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-ui-muted">
                  {g.section}
                </div>
              )}
              {g.section && <div className="xl:hidden my-2 border-t border-ui-line" />}
              {g.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  aria-label={item.label}
                  title={item.label}
                  data-testid={`nav-${item.to.slice(1)}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-3 h-12 transition-colors no-select ${
                      isActive
                        ? "bg-brand-button text-ui-text font-bold"
                        : "text-ui-muted hover:bg-ui-soft hover:text-ui-text"
                    }`
                  }
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  <span className="hidden xl:inline text-sm">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="p-2 xl:p-3 border-t border-ui-line">
          {user.role === 'admin' && <NavLink to="/audit-logs" aria-label="Log Aktivitas" title="Log Aktivitas" className={({isActive})=>`flex items-center gap-3 rounded-xl px-3 h-12 mb-2 ${isActive?'theme-nav-active':'text-ui-muted'}`}><ScrollText className="h-5 w-5 shrink-0"/><span className="hidden xl:inline text-sm">Log Aktivitas</span></NavLink>}
          <div
            className="hidden xl:flex items-center gap-3 px-3 py-2 mb-2 rounded-lg cursor-pointer select-none hover:bg-ui-soft"
            title="Tekan &amp; tahan untuk menampilkan tombol Keluar"
            onPointerDown={startLongPress}
            onPointerUp={cancelLongPress}
            onPointerLeave={cancelLongPress}
          >
            <div className="h-9 w-9 rounded-full bg-ui-muted-surface flex items-center justify-center text-sm font-bold">
              {user.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-[11px] text-brand-readable uppercase tracking-wide">
                {user.role === "admin" ? "Administrator" : user.role === "warehouse" ? "Gudang" : "Kasir"}
              </div>
            </div>
          </div>
          {showLogout ? (
            <Button
              data-testid="logout-button"
              variant="ghost"
              onClick={doLogout}
              className="w-full justify-center xl:justify-start gap-2 text-ui-muted hover:text-danger-readable hover:bg-danger-soft h-11"
            >
              <LogOut className="h-5 w-5" />
              <span className="hidden xl:inline">Keluar</span>
            </Button>
          ) : (
            <button
              data-testid="logout-reveal"
              onPointerDown={startLongPress}
              onPointerUp={cancelLongPress}
              onPointerLeave={cancelLongPress}
              className="w-full flex items-center justify-center gap-2 text-ui-muted hover:text-ui-muted h-8 text-[11px]"
              title="Tekan &amp; tahan untuk keluar"
            >
              <LogOut className="h-4 w-4 xl:hidden" />
              <span className="hidden xl:inline">Tekan &amp; tahan untuk keluar</span>
            </button>
          )}
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 overflow-hidden flex flex-col">
        <BrandIdentity />
        {readOnly && <header className="shrink-0 border-b border-warning bg-warning-soft px-3 py-2 flex flex-wrap items-center gap-2" data-testid="master-view-bar">
          <button type="button" onClick={() => setNavHidden(v => !v)} className="border rounded px-3 py-2 bg-ui-card text-sm" aria-label="Tampilkan atau sembunyikan menu">Menu</button>
          <span className="text-sm font-semibold">{tenant.name}</span>
          <NavLink className="text-sm underline text-brand-readable" to="/master/tenants">Kelola tenant</NavLink>
          <NavLink className="text-sm underline text-brand-readable" to="/master/system">Status VPS</NavLink>
          <label htmlFor="master-tenant" className="sr-only">Tenant yang ditampilkan</label>
          <select id="master-tenant" data-testid="tenant-selector" value={selectedTenant} onChange={e => selectMasterTenant(e.target.value)}
            className="w-full sm:w-64 h-10 rounded-lg border border-warning bg-ui-card px-2 text-sm">
            <option value="">Pilih tenant… (data kosong)</option>
            {masterTenants.map(t => <option key={t.id} value={t.id}>{t.name} · {t.host}</option>)}
          </select>
          {stores.length > 0 && <span className="text-sm text-ui-muted">{stores[0].name}</span>}
          <span role="status" className="text-xs text-warning-readable">{location.pathname.startsWith("/master/") ? "Administrasi master" : selectedTenant ? "Hanya baca" : "Pilih tenant untuk menampilkan data"}</span>
          {tenantListError && <span role="alert" className="text-sm text-danger-readable">{tenantListError}</span>}
        </header>}
        <Outlet context={{ navHidden, setNavHidden }} />
      </main>
    </div>
  );
}
